import { useMap } from '@vis.gl/react-google-maps';
import cx from 'clsx';
import React from 'react';

import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';

import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { initialSorting } from '@constants/tableSorting';

import useTranslation from '@hooks/useTranslation';

import type { IAssetTracking } from '@model/assetTracking';

import {
  selectActiveAssetInfo,
  selectAssetTableSorting,
  useAssetTrackerActions,
} from '../../store/useAssetTrackerStore';
import AssetContextMenu from '../AssetContextMenu/AssetContextMenu';
import AssetssTableSkeleton from './AssetssTableSkeleton';
import style from './style.module.scss';

interface IProps {
  assetList?: IAssetTracking[];
  isLoading: boolean;
}

const AssetsList: React.FC<IProps> = ({ assetList, isLoading }) => {
  const { t } = useTranslation();

  const map = useMap();

  const { setAssetTableSorting, setActiveAssetInfo } = useAssetTrackerActions();

  const activeAssetInfo = selectActiveAssetInfo();
  const sorting = selectAssetTableSorting();

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setAssetTableSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setAssetTableSorting(initialSorting);
    } else {
      setAssetTableSorting({ id, desc: true });
    }
  };

  const handleActiveAsset = (asset: IAssetTracking) => {
    if (asset.lastReport.location) {
      setActiveAssetInfo(asset);
      map?.setCenter(asset.lastReport.location);
      map?.setZoom(18);
    }
  };

  return (
    <div className={style.assetsListBlock}>
      <div className={style.blockHead}>
        <div className={style.blockTitle}>{t('Asset Tracking')}</div>
      </div>
      <div className={style.blockContainer}>
        <div className={style.tableWrapper}>
          <table className={style.assetsTable} cellSpacing="0" cellPadding="0">
            <thead className={style.assetsTableHead}>
              <tr>
                <th className={style.assetsTableTh}>
                  {t('Asset Tag')}
                  <TableSort
                    className={style.sortIcon}
                    onClick={() => onChangeSorting('assetTag')}
                    data-tooltip-content="assetTag"
                    data-tooltip-id="assets-sort-tooltip"
                  />
                </th>
                <th className={style.assetsTableTh}>
                  {t('Asset Tag ID')}
                  <TableSort
                    className={style.sortIcon}
                    onClick={() => onChangeSorting('externalAssetId')}
                    data-tooltip-content="externalAssetId"
                    data-tooltip-id="assets-sort-tooltip"
                  />
                </th>
                <th className={style.assetsTableTh}>
                  {t('Last update')}
                  <TableSort
                    className={style.sortIcon}
                    onClick={() => onChangeSorting('humanDate')}
                    data-tooltip-content="humanDate"
                    data-tooltip-id="assets-sort-tooltip"
                  />
                </th>
                <th className={style.assetsTableTh}></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <AssetssTableSkeleton />
              ) : assetList?.length ? (
                assetList?.map((asset) => (
                  <tr
                    key={asset.id}
                    className={cx(style.assetsTableBodyRow, {
                      [style.activeRow]: activeAssetInfo?.id === asset.id,
                    })}
                    onClick={() => {
                      handleActiveAsset(asset);
                    }}
                  >
                    <td className={style.assetsTableTd}>{asset.assetTag}</td>
                    <td className={style.assetsTableTd}>{asset.externalAssetId}</td>
                    <td className={style.assetsTableTd}>{asset.lastReport.humanDate}</td>
                    <td
                      className={cx(style.assetsTableTd, style.actionsIconContainer)}
                      data-tooltip-id="table-asset-menu"
                      data-tooltip-content={JSON.stringify(asset)}
                    >
                      <ThreeDotsIcon />
                    </td>
                  </tr>
                ))
              ) : (
                <TableEmptyRowExtended text={t('Tracked Assets not found.')} />
              )}
            </tbody>
          </table>
        </div>
      </div>
      <AssetContextMenu id="table-asset-menu" />
      <TooltipSort id="assets-sort-tooltip" sorting={sorting} />
    </div>
  );
};

export default AssetsList;
